import { useEffect, useState, useCallback, useMemo } from 'react';
import { Polyline, Popup, useMap, useMapEvents } from 'react-leaflet';
import { getTransmissionLines } from '../services/api';

// HIFLD electric power transmission-line overlay (#991).
//
// Renders transmission lines from the /transmission-lines GeoJSON
// endpoint. Each feature already carries a server-derived
// `properties.color` / `properties.weight` / `properties.dashed`
// (derived from the backend VOLT_CLASS_STYLE, one source of truth), so
// the client styles each line directly from those props rather than
// recomputing a color map. Voltage class encodes color; magnitude
// encodes weight; DC lines render dashed.
//
// Zoom-gating: transmission lines are dense, so below BACKBONE_ZOOM we
// draw only the high-voltage backbone (volt_class in BACKBONE_CLASSES)
// to keep the map legible; at higher zoom every line in the viewport
// renders.
//
// Visibility: self-managing via react-leaflet `overlayadd` /
// `overlayremove` events keyed on OVERLAY_NAME, mirroring GenerationLayer
// / InsarLayer. The component mounts inside a `<LayersControl.Overlay
// name={OVERLAY_NAME}>` block in MapView; toggling the standard
// map-overlay checkbox flips `enabled` here with no external state.

const OVERLAY_NAME = 'Transmission lines';

// Voltage-class swatches. Kept in sync with the backend VOLT_CLASS_STYLE
// (transmission_lines.py) so the API-supplied `properties.color` and this
// legend agree. Ordered low → high voltage for the legend.
export const VOLT_CLASS_META = [
  { key: 'UNDER 100',     color: '#9ecae1', label: 'Under 100 kV' },
  { key: '100-161',       color: '#41ab5d', label: '100-161 kV' },
  { key: '220-287',       color: '#fec44f', label: '220-287 kV' },
  { key: '345',           color: '#fe9929', label: '345 kV' },
  { key: '500',           color: '#e31a1c', label: '500 kV' },
  { key: '735 AND ABOVE', color: '#980043', label: '735 kV and above' },
  { key: 'DC',            color: '#00b8d4', label: 'DC' },
  { key: 'NOT AVAILABLE', color: '#bdbdbd', label: 'Not available' },
];

const ALL_VOLT_CLASSES = VOLT_CLASS_META.map((m) => m.key);

// Backbone = high-voltage classes shown even when zoomed out, so the
// grid's skeleton stays visible without drawing every distribution-scale
// line. Below BACKBONE_ZOOM only these classes render.
export const BACKBONE_CLASSES = new Set(['345', '500', '735 AND ABOVE', 'DC']);
export const BACKBONE_ZOOM = 9;

// Normalize a feature's volt_class to a legend key. Unknown / null volt
// classes fall through to 'NOT AVAILABLE' (the same bucket the backend's
// gray fallback color represents), so the toggle + legend cover them.
export function voltClassKey(voltClass) {
  if (!voltClass) return 'NOT AVAILABLE';
  const k = String(voltClass).trim().toUpperCase();
  return ALL_VOLT_CLASSES.includes(k) ? k : 'NOT AVAILABLE';
}

// Convert a GeoJSON LineString (or MultiLineString) `coordinates` array
// ([lon, lat] order) into Leaflet [lat, lon] positions. Returns an array
// of position-arrays (one per line part) so a MultiLineString renders as
// multiple <Polyline> segments.
export function toLatLngParts(geometry) {
  if (!geometry) return [];
  if (geometry.type === 'LineString') {
    return [(geometry.coordinates || []).map(([lon, lat]) => [lat, lon])];
  }
  if (geometry.type === 'MultiLineString') {
    return (geometry.coordinates || []).map(
      (part) => part.map(([lon, lat]) => [lat, lon]),
    );
  }
  return [];
}

function TransmissionLayer() {
  const map = useMap();
  const [enabled, setEnabled] = useState(false);
  const [zoom, setZoom] = useState(() => (map && map.getZoom ? map.getZoom() : 0));
  // Per-volt-class visibility. All classes on by default; the in-overlay
  // toggle flips individual entries so a user can isolate e.g. 500 kV.
  const [activeClasses, setActiveClasses] = useState(() => new Set(ALL_VOLT_CLASSES));
  const [features, setFeatures] = useState([]);

  const load = useCallback(async () => {
    if (!enabled) {
      setFeatures([]);
      return;
    }
    const b = map.getBounds();
    const bbox = `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`;
    try {
      // Fetch ALL classes (no server-side filter) and gate per-class +
      // zoom client-side, so toggling a class is instant and doesn't
      // re-hit the API. The response is capped at `limit` server-side.
      const data = await getTransmissionLines(bbox, {}, 4000);
      setFeatures(data?.features || []);
    } catch (err) {
      // Silent failure: an empty overlay is a valid "no data" signal and
      // a transient API blip shouldn't crash the map.
      // eslint-disable-next-line no-console
      console.warn('Transmission-lines overlay load failed:', err);
      setFeatures([]);
    }
  }, [enabled, map]);

  useMapEvents({
    moveend: load,
    zoomend: () => { setZoom(map.getZoom()); load(); },
    overlayadd: (e) => { if (e.name === OVERLAY_NAME) setEnabled(true); },
    overlayremove: (e) => { if (e.name === OVERLAY_NAME) setEnabled(false); },
  });

  useEffect(() => { load(); }, [load]);

  const toggleClass = useCallback((cls) => {
    setActiveClasses((prev) => {
      const next = new Set(prev);
      if (next.has(cls)) next.delete(cls); else next.add(cls);
      return next;
    });
  }, []);

  // Apply the per-class toggle AND the zoom gate: below BACKBONE_ZOOM
  // only backbone classes draw, regardless of the toggle state.
  const visibleFeatures = useMemo(() => {
    const backboneOnly = zoom < BACKBONE_ZOOM;
    return features.filter((f) => {
      const key = voltClassKey(f?.properties?.volt_class);
      if (!activeClasses.has(key)) return false;
      if (backboneOnly && !BACKBONE_CLASSES.has(key)) return false;
      return true;
    });
  }, [features, activeClasses, zoom]);

  if (!enabled) return null;

  return (
    <>
      {/* Per-volt-class on/off toggle, rendered as a compact control.
          Only shown while the overlay is on. */}
      <TransmissionClassToggle activeClasses={activeClasses} onToggle={toggleClass} />

      {visibleFeatures.map((f) => {
        const p = f.properties || {};
        const parts = toLatLngParts(f.geometry);
        if (parts.length === 0) return null;
        const dashArray = p.dashed ? '6,6' : null;
        return parts.map((positions, idx) => {
          if (!positions || positions.length < 2) return null;
          return (
            <Polyline
              key={`tl-${p.id}-${idx}`}
              positions={positions}
              pathOptions={{
                color: p.color || '#bdbdbd',
                weight: p.weight || 1,
                dashArray,
                opacity: 0.9,
              }}
            >
              <Popup maxWidth={280} minWidth={200}>
                <div style={{ fontSize: '0.82rem', lineHeight: 1.45 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span
                      style={{
                        display: 'inline-block', width: 14, height: 4,
                        background: p.color || '#bdbdbd', flexShrink: 0,
                        borderRadius: 1,
                      }}
                    />
                    <span style={{ fontWeight: 700, color: '#1a1a2e' }}>
                      {p.volt_class ? `${p.volt_class} kV class` : 'Transmission line'}
                    </span>
                  </div>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ paddingRight: 8, color: '#666' }}>Voltage</td>
                        <td style={{ textAlign: 'right' }}>{p.voltage ? `${p.voltage} kV` : '—'}</td>
                      </tr>
                      <tr>
                        <td style={{ paddingRight: 8, color: '#666' }}>Type</td>
                        <td style={{ textAlign: 'right' }}>{p.line_type || '—'}</td>
                      </tr>
                      <tr>
                        <td style={{ paddingRight: 8, color: '#666' }}>Status</td>
                        <td style={{ textAlign: 'right' }}>{p.status || '—'}</td>
                      </tr>
                      <tr>
                        <td style={{ paddingRight: 8, color: '#666' }}>Operator</td>
                        <td style={{ textAlign: 'right' }}>{p.operator || '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                  {p.source && (
                    <div style={{ marginTop: 6, fontSize: '0.68rem', color: '#888' }}>
                      Source: {p.source}
                    </div>
                  )}
                </div>
              </Popup>
            </Polyline>
          );
        });
      })}
    </>
  );
}

// Per-volt-class on/off toggle. Rendered inside the map
// (absolute-positioned) only while the overlay is enabled. Anchored to
// the right so it doesn't collide with the generation-tech toggle
// (top-left). `top: 64` drops it BELOW Leaflet's native layers control
// (top-right corner, ~48px tall with touch targets) so it no longer
// covers the base-/overlay-layer picker (#1037).
export function TransmissionClassToggle({ activeClasses, onToggle }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 64,
        right: 12,
        zIndex: 1000,
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: '6px 8px',
        fontSize: '0.72rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        maxHeight: 260,
        overflowY: 'auto',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Voltage class</div>
      {VOLT_CLASS_META.map(({ key, color, label }) => {
        const on = activeClasses.has(key);
        return (
          <label
            key={key}
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 2 }}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => onToggle(key)}
              aria-label={`Toggle ${label} transmission lines`}
            />
            <span
              style={{
                display: 'inline-block', width: 14, height: 4, background: color,
                border: '1px solid #333', flexShrink: 0, borderRadius: 1,
              }}
            />
            <span>{label}</span>
          </label>
        );
      })}
    </div>
  );
}

// Collapsible legend, self-managing visibility via the same overlayadd/
// remove signal as the layer. Render once inside MapContainer (alongside
// TransmissionLayer); it appears only when the overlay is on. Collapsed
// to a header row by default; click the header to expand the swatches +
// the AC-solid / DC-dashed line-style key.
// Anchored bottom-LEFT (alongside the InSAR / generation legends) rather
// than bottom-right so it no longer overlaps the always-on Parcels legend
// (bottom-right) — #1037.
export function TransmissionLegend() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useMapEvents({
    overlayadd: (e) => { if (e.name === OVERLAY_NAME) setVisible(true); },
    overlayremove: (e) => { if (e.name === OVERLAY_NAME) setVisible(false); },
  });

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 24,
        left: 12,
        zIndex: 1000,
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: '8px 10px',
        fontSize: '0.72rem',
        lineHeight: 1.4,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        maxWidth: 220,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          all: 'unset',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          fontWeight: 700,
        }}
      >
        <span style={{ fontSize: '0.66rem' }}>{expanded ? '▾' : '▸'}</span>
        Transmission lines
      </button>
      {expanded && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: '#666', marginBottom: 6, fontSize: '0.68rem' }}>
            Color ∝ voltage class; thicker ∝ higher voltage
          </div>
          {VOLT_CLASS_META.map(({ key, color, label }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span
                style={{
                  display: 'inline-block', width: 16, height: 4, background: color,
                  border: '1px solid #333', flexShrink: 0, borderRadius: 1,
                }}
              />
              <span>{label}</span>
            </div>
          ))}
          {/* Line-style key: AC renders solid, DC dashed. */}
          <div style={{ marginTop: 6, borderTop: '1px solid #eee', paddingTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <svg width="20" height="6" aria-hidden="true">
                <line x1="0" y1="3" x2="20" y2="3" stroke="#333" strokeWidth="2" />
              </svg>
              <span>AC (solid)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="20" height="6" aria-hidden="true">
                <line x1="0" y1="3" x2="20" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="4,3" />
              </svg>
              <span>DC (dashed)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { OVERLAY_NAME as TRANSMISSION_OVERLAY_NAME };
export default TransmissionLayer;
